import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';
import downloadFile from '../utils/download-file.ts';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import { SkipCache } from '@warp-drive/core-types/request';
import type {
  SqlDataset,
  SqlDatasetFieldCatalogEntry,
  SqlDatasetGuardrails,
  SqlDatasetStatus,
  SqlDatasetTenantBinding,
} from '../data/resources/sql-dataset.type.ts';
import type { SqlDatasetRevision } from '../data/resources/sql-dataset-revision.type.ts';

/**
 * sql-datasets is a v2-namespaced JSONAPI resource (/api/v2/sql-datasets,
 * tio-api RFC 062) — unlike the root-mounted v1 resources the global
 * `setBuildURLConfig({ namespace: '' })` serves, so every request carries
 * an explicit resourcePath. Save bodies are hand-built (the same pattern
 * as tio-reporting requests/report.ts — the generic createRecord/
 * updateRecord builders produce no body in this app's handler chain).
 * Attribute keys stay camelized to match the store's default
 * `x-json-api-key-format: camelized` round-trip.
 */
const RESOURCE_PATH = 'api/v2/sql-datasets';

export type SqlDatasetAttributes = {
  slug: string;
  label: string;
  description: string | null;
  product: string | null;
  baseQuery: string;
  fieldCatalog: SqlDatasetFieldCatalogEntry[];
  tenantBinding: SqlDatasetTenantBinding | null;
  allowedRoles: string[];
  guardrails: SqlDatasetGuardrails;
  uniqueKeyColumn: string | null;
  status?: SqlDatasetStatus;
};

/**
 * Authoring reads force a reload (NOT SkipCache): saves are hand-built
 * requests whose responses never enter the document cache, so a cached
 * read (5-minute soft TTL) would render pre-save values — and re-saving
 * from that form state would silently revert the author's own edit.
 * `reload` refetches every time but still upserts into the cache, so
 * `content.data` stays materialized reactive records; SkipCache would
 * hand back raw JSON:API resource objects and break every `.slug` read.
 */
export function sqlDatasetQuery() {
  return {
    ...query<SqlDataset>('sql-dataset', {}, { resourcePath: RESOURCE_PATH }),
    cacheOptions: { reload: true },
  };
}

/**
 * The report builder's query surface for SQL datasets (mirrors the
 * /api/v2/search contract; responses are the OpenSearch envelope with the
 * same camelization as the OS path, so the builder's parsers work as-is).
 */
export function sqlSearchFieldsRequest(datasetSlug: string): RequestInfo {
  const url = `${buildBaseURL({ resourcePath: 'api/v2/sql-search' })}/fields?dataset=${encodeURIComponent(datasetSlug)}`;
  return { url, method: 'GET', cacheOptions: { [SkipCache]: true } };
}

export function sqlSearchRequest(options: {
  dataset: string;
  /** Omit for cross-company queries on column-kind datasets (backend authorizes). */
  companyId?: string | number;
  searchBody: Record<string, unknown>;
}): RequestInfo {
  const companyId = options.companyId === '' ? undefined : options.companyId;
  return {
    url: buildBaseURL({ resourcePath: 'api/v2/sql-search' }),
    method: 'POST',
    body: JSON.stringify({
      dataset: options.dataset,
      ...(companyId == null ? {} : { company_id: companyId }),
      search_body: options.searchBody,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** Forced reload for the same save-revert reason as {@link sqlDatasetQuery}. */
export function sqlDatasetFindRecord(id: string) {
  return {
    ...findRecord<SqlDataset>('sql-dataset', id, { resourcePath: RESOURCE_PATH }),
    cacheOptions: { reload: true },
  };
}

export function sqlDatasetCreate(attributes: SqlDatasetAttributes): RequestInfo {
  return {
    url: buildBaseURL({ resourcePath: RESOURCE_PATH }),
    method: 'POST',
    // Schema-format (singular) type: TioTypeNormalizationHandler pluralizes
    // (and camelizes under camelized negotiation) for the wire.
    body: JSON.stringify({ data: { type: 'sql-dataset', attributes } }),
    cacheOptions: { [SkipCache]: true },
  };
}

export function sqlDatasetUpdate(id: string, attributes: SqlDatasetAttributes): RequestInfo {
  return {
    url: `${buildBaseURL({ resourcePath: RESOURCE_PATH })}/${id}`,
    method: 'PATCH',
    body: JSON.stringify({ data: { id, type: 'sql-dataset', attributes } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Draft revisions (RFC 062 A2) carry authoring fields without slug, status, or explainSnapshot.
 * Only /explain persists gate evidence. Open and republish commands belong to the parent dataset
 * and use SqlDatasetActionsService.
 */
const REVISION_RESOURCE_PATH = 'api/v2/sql-dataset-revisions';

export type SqlDatasetRevisionAttributes = {
  label: string;
  description: string | null;
  product: string | null;
  baseQuery: string;
  fieldCatalog: SqlDatasetFieldCatalogEntry[];
  tenantBinding: SqlDatasetTenantBinding | null;
  allowedRoles: string[];
  guardrails: SqlDatasetGuardrails;
  uniqueKeyColumn: string | null;
};

/** Forced reload for the same save-revert reason as {@link sqlDatasetQuery}. */
export function sqlDatasetRevisionFindRecord(id: string) {
  return {
    ...findRecord<SqlDatasetRevision>('sql-dataset-revision', id, {
      resourcePath: REVISION_RESOURCE_PATH,
    }),
    cacheOptions: { reload: true },
  };
}

export function sqlDatasetRevisionUpdate(
  id: string,
  attributes: SqlDatasetRevisionAttributes
): RequestInfo {
  return {
    url: `${buildBaseURL({ resourcePath: REVISION_RESOURCE_PATH })}/${id}`,
    method: 'PATCH',
    body: JSON.stringify({ data: { id, type: 'sql-dataset-revision', attributes } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** Discard a pending revision (the parent is untouched). Server returns 204. */
export function sqlDatasetRevisionDelete(id: string): RequestInfo {
  return {
    url: `${buildBaseURL({ resourcePath: REVISION_RESOURCE_PATH })}/${id}`,
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

export interface SqlReportExportResponse {
  signed_url: string;
  filename: string;
  row_count: number;
  /** True when the export_row_cap guardrail cut the result short. */
  truncated?: boolean;
  asset_id?: string;
}

/**
 * POST /api/v2/reports/:id/sql-export — synchronous CSV export for a saved
 * report backed by a SQL dataset (RFC 062 WS-9). Mirrors `exportReport`'s
 * contract and download flow, but executes on the sql-search vertical:
 * keyset batches on the dataset's stable unique key (no held cursor).
 * `search_body` is REQUIRED (passthrough mode only) and company scoping is
 * the request param, never a body filter. `widgetId` (`widget_id`) exports one
 * dashboard widget; this path runs the body's own `aggs`, unlike `exportReport`.
 */
export async function sqlExportReport(
  reportId: string,
  options: {
    apiKey: string;
    dataset: string;
    searchBody: Record<string, unknown>;
    companyId?: string;
    widgetId?: string;
    columnKeys?: string[];
    columnLabels?: Record<string, string>;
    columnFormats?: Record<string, unknown>;
  }
): Promise<SqlReportExportResponse> {
  const headers = buildTioAuthHeaders({
    apiKey: options.apiKey,
    contentType: 'application/json',
  });

  const reportsBase = buildBaseURL({ resourcePath: 'api/v2/reports' });
  const response = await fetch(`${reportsBase}/${reportId}/sql-export`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      dataset: options.dataset,
      search_body: options.searchBody,
      ...(options.companyId ? { company_id: options.companyId } : {}),
      ...(options.widgetId ? { widget_id: options.widgetId } : {}),
      ...(options.columnKeys ? { column_keys: options.columnKeys } : {}),
      // Empty label/format maps are omitted so the body stays clean when the
      // report has no per-column config — the server falls back to catalog
      // labels for headers (it has NO fallback for formats, so an empty
      // formats map means raw values: cents, enum codes, ISO dates).
      ...(options.columnLabels && Object.keys(options.columnLabels).length
        ? { column_labels: options.columnLabels }
        : {}),
      ...(options.columnFormats && Object.keys(options.columnFormats).length
        ? { column_formats: options.columnFormats }
        : {}),
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Export failed (${response.status})`);
  }

  const result = (await response.json()) as SqlReportExportResponse;

  // Same delivery as exportReport: a failed signed-URL download must reject,
  // not silently report success with no CSV.
  const fileResponse = await fetch(result.signed_url);
  if (!fileResponse.ok) {
    throw new Error(`Export download failed (${fileResponse.status})`);
  }
  const blob = await fileResponse.blob();
  downloadFile(blob, result.filename, 'text/csv');

  return result;
}
